import React, {useEffect, useState} from 'react'
import Swal from 'sweetalert2'
import {
  ILaudoAuditoria,
  ILaudoResponsaveis,
  ILaudoUsuarioOpcao,
} from '../interfaces/ILaudoAuditoria'
import {LaudoAuditoriaService} from '../services/LaudoAuditoriaService'

interface Props {
  periodo: string
  responsaveis: ILaudoResponsaveis
  onSalvo: (laudo: ILaudoAuditoria) => void
}

type Papel = 'responsavelTecnicoId' | 'elaboradorId' | 'revisorId' | 'aprovadorId'

const PAPEIS: {campo: Papel; rotulo: string}[] = [
  {campo: 'responsavelTecnicoId', rotulo: 'Responsável técnico'},
  {campo: 'elaboradorId', rotulo: 'Elaborador'},
  {campo: 'revisorId', rotulo: 'Revisor'},
  {campo: 'aprovadorId', rotulo: 'Aprovador'},
]

const escolhidos = (r: ILaudoResponsaveis): Record<Papel, number | ''> => ({
  responsavelTecnicoId: r.responsavelTecnicoId ?? '',
  elaboradorId: r.elaboradorId ?? '',
  revisorId: r.revisorId ?? '',
  aprovadorId: r.aprovadorId ?? '',
})

// DEVN3-521 — primeira etapa da revisão do laudo: quem é o responsável técnico, o elaborador, o
// revisor e o aprovador (usuários ativos do cliente). Sem isso salvo, nada é revisado nem
// exportado. Os nomes saem na "Identificação do Responsável Técnico" do PDF, e a data em que
// foram salvos é a "Data de aprovação". Pode ser alterado depois; a data acompanha.
const AuditoriaLaudoResponsaveis: React.FC<Props> = ({periodo, responsaveis, onSalvo}) => {
  const [usuarios, setUsuarios] = useState<ILaudoUsuarioOpcao[]>([])
  const [carregando, setCarregando] = useState(true)
  const [editando, setEditando] = useState(!responsaveis.configurado)
  const [valores, setValores] = useState(escolhidos(responsaveis))
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    let cancelado = false
    LaudoAuditoriaService.listarUsuarios()
      .then((lista) => !cancelado && setUsuarios(lista))
      .catch(() => undefined)
      .finally(() => !cancelado && setCarregando(false))
    return () => {
      cancelado = true
    }
  }, [])

  // O laudo é recarregado periodicamente pela tela; só ressincroniza o formulário quando o que
  // está salvo (ou a sugestão) realmente muda, para não apagar uma escolha em andamento.
  useEffect(() => {
    setValores(escolhidos(responsaveis))
    setEditando(!responsaveis.configurado)
  }, [
    responsaveis.configurado,
    responsaveis.dataConfiguracao,
    responsaveis.responsavelTecnicoId,
    responsaveis.elaboradorId,
    responsaveis.revisorId,
    responsaveis.aprovadorId,
  ])

  const usuario = (id: number | '' | null | undefined) => usuarios.find((u) => u.id === id)
  const completo = PAPEIS.every((p) => valores[p.campo] !== '')
  const tecnico = usuario(valores.responsavelTecnicoId)

  const salvar = async () => {
    if (!completo) return
    setSalvando(true)
    try {
      onSalvo(
        await LaudoAuditoriaService.salvarResponsaveis(periodo, {
          responsavelTecnicoId: Number(valores.responsavelTecnicoId),
          elaboradorId: Number(valores.elaboradorId),
          revisorId: Number(valores.revisorId),
          aprovadorId: Number(valores.aprovadorId),
        })
      )
    } catch (e: any) {
      Swal.fire(
        'Erro',
        e?.response?.data?.mensagem ?? 'Não foi possível salvar os responsáveis do laudo.',
        'error'
      )
    } finally {
      setSalvando(false)
    }
  }

  if (!editando) {
    const data = responsaveis.dataConfiguracao
      ? new Date(responsaveis.dataConfiguracao).toLocaleString('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'short',
        })
      : '—'
    const registro = usuario(responsaveis.responsavelTecnicoId)?.registroProfissional
    return (
      <div className='auditoria-card'>
        <div className='d-flex justify-content-between align-items-start'>
          <h5 className='mb-3'>Responsáveis do laudo</h5>
          <button className='btn btn-sm btn-outline-secondary' onClick={() => setEditando(true)}>
            Alterar responsáveis
          </button>
        </div>
        <dl className='row mb-0' style={{fontSize: 13}}>
          {PAPEIS.map((p) => (
            <React.Fragment key={p.campo}>
              <dt className='col-sm-3 text-muted'>{p.rotulo}</dt>
              <dd className='col-sm-9 mb-1'>
                {usuario(responsaveis[p.campo])?.nome ?? (carregando ? '…' : 'Usuário inativo')}
                {p.campo === 'responsavelTecnicoId' && !carregando && (
                  <span className='text-muted'>
                    {' '}
                    · Registro profissional: {registro || 'não cadastrado'}
                  </span>
                )}
              </dd>
            </React.Fragment>
          ))}
          <dt className='col-sm-3 text-muted'>Data de aprovação</dt>
          <dd className='col-sm-9 mb-0'>{data}</dd>
        </dl>
      </div>
    )
  }

  return (
    <div className='auditoria-card' style={{borderLeft: '3px solid #005E96'}}>
      <h5 className='mb-1'>Responsáveis do laudo</h5>
      <p className='text-muted' style={{fontSize: 13}}>
        {responsaveis.configurado
          ? 'Ao salvar, a data de aprovação do laudo passa a ser a desta alteração.'
          : 'Primeira etapa da revisão: defina quem responde por este laudo. Os nomes saem na identificação do responsável técnico do PDF, e a data em que você salvar é a data de aprovação.'}
      </p>
      {!responsaveis.configurado && responsaveis.sugestao && (
        <p style={{fontSize: 12, color: '#8a6d1d'}}>
          Preenchido com os responsáveis do último laudo desta empresa. Confira e salve.
        </p>
      )}

      <div className='row g-3'>
        {PAPEIS.map((p) => (
          <div className='col-md-6' key={p.campo}>
            <label className='auditoria-label' htmlFor={`laudo-${p.campo}`}>
              {p.rotulo}
            </label>
            <select
              id={`laudo-${p.campo}`}
              className='auditoria-select'
              disabled={carregando || salvando}
              value={valores[p.campo]}
              onChange={(e) => {
                // React 16 recicla o evento sintético: o valor tem que ser lido aqui, não dentro
                // do updater (que roda depois, com e.target já nulo).
                const valor = e.target.value
                setValores((v) => ({...v, [p.campo]: valor ? Number(valor) : ''}))
              }}
            >
              <option value=''>{carregando ? 'Carregando…' : 'Selecione'}</option>
              {usuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nome}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {tecnico && !tecnico.registroProfissional && (
        <div className='alert alert-warning mt-3 mb-0' style={{fontSize: 13}}>
          {tecnico.nome} não tem Registro Profissional no cadastro de usuários. O PDF vai sair com
          esse campo como “não informado” até o cadastro ser preenchido.
        </div>
      )}

      <div className='d-flex gap-2 mt-3'>
        <button className='btn btn-sm btn-primary' disabled={!completo || salvando} onClick={salvar}>
          {salvando && <span className='spinner-border spinner-border-sm me-1' />}
          Salvar responsáveis
        </button>
        {responsaveis.configurado && (
          <button
            className='btn btn-sm btn-outline-secondary'
            disabled={salvando}
            onClick={() => {
              setValores(escolhidos(responsaveis))
              setEditando(false)
            }}
          >
            Cancelar
          </button>
        )}
      </div>
    </div>
  )
}

export default AuditoriaLaudoResponsaveis
